import { Menu, MessageCircle, X } from 'lucide-react';
import { useState } from 'react';

import { Link } from '@/core/i18n/navigation';
import { whatsappUrl as whatsappUrlZh, whatsappUrlEn } from '@/lib/whatsapp';

type PublicSiteHeaderProps = {
  locale?: 'zh' | 'en';
  active?: 'services' | 'pricing' | 'cases' | 'blog';
  enHref?: string;
  zhHref?: string;
};

export function PublicSiteHeader({
  locale = 'zh',
  active,
  enHref = '/en',
  zhHref = '/',
}: PublicSiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const isZh = locale === 'zh';
  const whatsappHref = isZh ? whatsappUrlZh : whatsappUrlEn;
  const items = isZh
    ? [
        { key: 'services', href: '/services', label: '服務' },
        { key: 'pricing', href: '/pricing', label: '收費' },
        { key: 'cases', href: '/cases', label: '案例' },
        { key: 'blog', href: '/blog', label: '網誌' },
        { key: 'faq', href: '/#faq', label: 'FAQ' },
        { key: 'contact', href: '/#contact', label: '聯絡' },
      ]
    : [
        { key: 'services', href: '/en/services', label: 'Services' },
        { key: 'pricing', href: '/en/pricing', label: 'Pricing' },
        { key: 'cases', href: '/en/cases', label: 'Cases' },
        { key: 'blog', href: '/en/blog', label: 'Blog' },
        { key: 'faq', href: '/en#faq', label: 'FAQ' },
        { key: 'contact', href: '/en#contact', label: 'Contact' },
      ];

  return (
    <header className="sticky top-0 z-40 border-b border-[#143d35]/14 bg-doq-surface/95 backdrop-blur-xl">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-[#063d36] focus:px-4 focus:py-2 focus:text-white">
        {isZh ? '跳到主要內容' : 'Skip to main content'}
      </a>
      <div className="grid min-h-20 grid-cols-[1fr_auto] items-center gap-3 px-4 sm:px-8 lg:grid-cols-[260px_1fr_auto] lg:px-14">
        <Link to={isZh ? '/' : '/en'} className="flex min-w-0 items-center gap-3" aria-label={isZh ? '渡橋 Doq Creative 首頁' : 'Doq Creative home'}>
          <img src="/images/doq-logo.webp" alt="" width="512" height="512" className="size-10 shrink-0 object-cover" />
          <span className="flex min-w-0 flex-col leading-none">
            <span className="text-xl font-semibold tracking-[-0.04em] text-[#062f2b]">{isZh ? '渡橋' : 'Doq Creative'}</span>
            <span className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#344840]">{isZh ? 'Doq Creative' : 'Hong Kong'}</span>
          </span>
        </Link>

        <nav className="hidden justify-center gap-6 lg:flex xl:gap-8" aria-label={isZh ? '主要導覽' : 'Main navigation'}>
          {items.map((item) => (
            <Link key={item.key} to={item.href} className={`text-sm font-medium transition hover:text-[#815321] ${active === item.key ? 'text-[#815321]' : 'text-[#263a34]'}`}>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden lg:block"><LanguageSwitch locale={locale} enHref={enHref} zhHref={zhHref} /></div>
          <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="hidden h-12 items-center justify-center gap-2 bg-[#815321] px-5 text-sm font-semibold text-white transition hover:bg-[#6f451c] lg:inline-flex">
            {isZh ? 'WhatsApp 查詢' : 'WhatsApp enquiry'}
            <MessageCircle className="size-4" />
          </a>
          <button type="button" onClick={() => setOpen((value) => !value)} className="grid size-11 place-items-center border border-[#143d35]/20 text-[#063d36] lg:hidden" aria-label={open ? (isZh ? '關閉選單' : 'Close menu') : (isZh ? '開啟選單' : 'Open menu')} aria-expanded={open} aria-controls="mobile-public-menu">
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open ? (
        <div id="mobile-public-menu" className="border-t border-[#143d35]/14 px-4 pb-5 pt-3 lg:hidden">
          <nav className="grid grid-cols-2 gap-px bg-[#143d35]/14" aria-label={isZh ? '手機導覽' : 'Mobile navigation'}>
            {items.map((item) => (
              <Link key={item.key} to={item.href} onClick={() => setOpen(false)} className={`bg-doq-surface px-4 py-3 text-sm font-medium ${active === item.key ? 'text-[#815321]' : 'text-[#263a34]'}`}>
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="mt-3 flex items-center gap-3">
            <LanguageSwitch locale={locale} enHref={enHref} zhHref={zhHref} />
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 flex-1 items-center justify-center gap-2 bg-[#815321] px-4 text-sm font-semibold text-white">
              {isZh ? 'WhatsApp 查詢' : 'WhatsApp enquiry'}
              <MessageCircle className="size-4" />
            </a>
          </div>
        </div>
      ) : null}
    </header>
  );
}

function LanguageSwitch({ locale, enHref, zhHref }: { locale: 'zh' | 'en'; enHref: string; zhHref: string }) {
  return (
    <div className="inline-flex h-10 shrink-0 items-center border border-[#143d35]/18 bg-[#f1eadc] p-1 text-xs font-semibold" aria-label="Language switcher">
      {locale === 'zh' ? <span className="grid h-8 min-w-8 place-items-center bg-[#063d36] px-2 text-[#fbf5ea]">中</span> : <Link to={zhHref} className="grid h-8 min-w-8 place-items-center px-2 text-[#263a34]">中</Link>}
      {locale === 'en' ? <span className="grid h-8 min-w-8 place-items-center bg-[#063d36] px-2 text-[#fbf5ea]">EN</span> : <Link to={enHref} className="grid h-8 min-w-8 place-items-center px-2 text-[#263a34]">EN</Link>}
    </div>
  );
}
